import React, { useState, useEffect, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const LABELS = { gas_safety: 'Gas Safety (CP12)', eicr: 'EICR', epc: 'EPC' };
export const FIELD = { color: '#0f172a', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 4, padding: '4px 6px' };

// Compliance renewal harness — every button runs the PRODUCTION scanner
// with a faked date (or the real scheduler check). Harness clients only.
export default function ComplianceHarnessPanel({ adminKey, harnessClients = [] }) {
  const [clientId, setClientId] = useState('');
  const [setup, setSetup] = useState(null);
  const [propertyId, setPropertyId] = useState('');
  const [docType, setDocType] = useState('gas_safety');
  const [docId, setDocId] = useState('');
  const [mrRef, setMrRef] = useState('');
  const [busy, setBusy] = useState('');
  const [out, setOut] = useState(null);
  const headers = { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (!harnessClients.some(c => c.id === clientId)) setClientId(harnessClients[0]?.id || '');
  }, [harnessClients, clientId]);

  const loadSetup = useCallback(async (selectDoc) => {
    if (!clientId) { setSetup(null); return; }
    const res = await fetch(`${API_BASE_URL}/api/admin/harness/compliance/setup?client_id=${clientId}`,
      { headers: { 'X-Admin-Key': adminKey } });
    if (!res.ok) { setSetup(null); return; }
    const data = await res.json();
    setSetup(data);
    setPropertyId(p => (data.properties.some(x => x.id === p) ? p : data.properties[0]?.id || ''));
    setDocId(d => selectDoc || (data.certificates.some(c => c.id === d) ? d : data.certificates[0]?.id || ''));
  }, [clientId, adminKey]);

  useEffect(() => { loadSetup(); }, [loadSetup]);

  const call = async (label, path, method = 'POST', body = null) => {
    setBusy(label); setOut(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/harness/compliance/${path}`,
        { method, headers, body: body ? JSON.stringify(body) : undefined });
      const data = await res.json();
      setOut({ ok: res.ok, label, data });
      if (res.ok && data.renewal_job?.contractor_ref) setMrRef(data.renewal_job.contractor_ref);
      if (res.ok) loadSetup(data.doc_id);
    } catch (e) {
      setOut({ ok: false, label, data: String(e) });
    } finally { setBusy(''); }
  };

  const cert = (days) => call(`cert-${days}`, 'test-certificate', 'POST',
    { client_id: clientId, doc_type: docType, days_to_expiry: days, property_id: propertyId || null });
  const fire = (n) => call(`fire-${n}`, 'fire', 'POST',
    { client_id: clientId, doc_id: docId, days_before_expiry: n });
  const selectedCert = setup?.certificates.find(c => c.id === docId);

  if (!harnessClients.length) {
    return (
      <section style={{ marginTop: '2rem' }} data-testid="compliance-harness-panel">
        <h3>Compliance Renewal Harness</h3>
        <p data-testid="compliance-harness-no-client"><em>Mark an account as a harness client above first.</em></p>
      </section>
    );
  }

  return (
    <section style={{ marginTop: '2rem' }} data-testid="compliance-harness-panel">
      <h3>Compliance Renewal Harness</h3>
      <p style={{ color: '#64748b', fontSize: '.85em', maxWidth: 760 }}>
        Each trigger runs the production renewal scanner as if today were N days before the selected
        certificate's expiry. Everything downstream is live code.
      </p>
      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <label>Harness client{' '}
          <select value={clientId} onChange={e => setClientId(e.target.value)} data-testid="compliance-harness-client-select" style={FIELD}>
            {harnessClients.map(c => <option key={c.id} value={c.id}>{c.company_name} — {c.contact_email}</option>)}
          </select>
        </label>
        <label>Property{' '}
          <select value={propertyId} onChange={e => setPropertyId(e.target.value)} data-testid="compliance-harness-property-select" style={FIELD}>
            {(setup?.properties || []).length === 0 && <option value="">No property on this account</option>}
            {(setup?.properties || []).map(p => <option key={p.id} value={p.id}>{p.address}</option>)}
          </select>
        </label>
      </div>
      {setup && <Readiness setup={setup} docType={selectedCert?.doc_type || docType} />}
      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', alignItems: 'center', marginTop: '.6rem' }}>
        <select value={docType} onChange={e => setDocType(e.target.value)} data-testid="compliance-harness-doc-type" style={FIELD}>
          {Object.entries(LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button disabled={!!busy || !propertyId} onClick={() => cert(30)} data-testid="compliance-harness-create-cert">
          Create test certificate (30 days out)
        </button>
        <button disabled={!!busy || !propertyId} onClick={() => cert(20)} data-testid="compliance-harness-create-genuine">
          Create GENUINE 20-day certificate
        </button>
      </div>
      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', alignItems: 'center', marginTop: '.6rem' }}>
        <label>Certificate{' '}
          <select value={docId} onChange={e => setDocId(e.target.value)} data-testid="compliance-harness-cert-select" style={FIELD}>
            {(setup?.certificates || []).length === 0 && <option value="">No certificate yet — create one above</option>}
            {(setup?.certificates || []).map(c => (
              <option key={c.id} value={c.id}>
                {LABELS[c.doc_type] || c.doc_type} — expires {c.expiry_date} — {c.property}{c.is_test ? ' [test]' : ''}
              </option>
            ))}
          </select>
        </label>
        {[20, 10, 5, 0].map(n => (
          <button key={n} disabled={!!busy || !docId} onClick={() => fire(n)} data-testid={`compliance-harness-fire-${n}`}>
            {n === 20 ? 'Fire 20-day trigger' : n === 0 ? 'Fire expiry day' : `Fire ${n}-day escalation`}
          </button>
        ))}
        <button disabled={!!busy || !docId}
          onClick={() => call('sched', `scheduler-check/${docId}?client_id=${encodeURIComponent(clientId)}`, 'GET')}
          data-testid="compliance-harness-scheduler-check">
          Scheduler check (genuine)
        </button>
      </div>
      <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center', marginTop: '.6rem' }}>
        <label>Renewal job{' '}
          <input placeholder="filled in after Fire 20-day trigger" value={mrRef} onChange={e => setMrRef(e.target.value)}
            data-testid="compliance-harness-mr-ref" style={{ ...FIELD, fontFamily: 'monospace', minWidth: 240 }} />
        </label>
        <button disabled={!!busy || !mrRef} data-testid="compliance-harness-cascade"
          onClick={() => call('cascade', 'cascade', 'POST', { client_id: clientId, mr_ref: mrRef.trim() })}>
          Fire 24h cascade
        </button>
      </div>
      {out && (
        <pre data-testid="compliance-harness-result"
          style={{ background: out.ok ? '#f0fdf4' : '#fef2f2', padding: '.6rem', fontSize: '.8em', marginTop: '.75rem',
                   maxHeight: 320, overflow: 'auto' }}>
          {out.label}: {JSON.stringify(out.data, null, 2)}
        </pre>
      )}
    </section>
  );
}

function Readiness({ setup, docType }) {
  const n = setup.contractors_by_cert_type?.[docType] || 0;
  const line = (ok, okText, badText, id) => (
    <li data-testid={id} style={{ color: ok ? '#15803d' : '#b91c1c' }}>{ok ? '✓ ' : '✗ '}{ok ? okText : badText}</li>
  );
  return (
    <ul data-testid="compliance-harness-readiness" style={{ fontSize: '.85em', margin: '.6rem 0 0', paddingLeft: '1.2rem' }}>
      <li data-testid="readiness-email-destination" style={{ color: setup.redirect_emails ? '#f59e0b' : '#93c5fd', fontWeight: 700 }}>
        {setup.redirect_emails
          ? '• Emails: REDIRECTED to the harness inbox (subjects start [HARNESS])'
          : '• Emails: sent to the REAL addresses on this account (contractor, tenant, you)'}
      </li>
      {line(setup.properties.length > 0, 'Account has a property', 'Account has no property — add one in the app', 'readiness-property')}
      {line(setup.compliance_automated, 'Compliance renewals: Estatel AI manages (and maintenance too)',
        'Compliance renewals are NOT automated — set both Maintenance and Compliance renewals to "Estatel AI manages" in Settings',
        'readiness-autonomy')}
      {line(n > 0, `${n} contractor(s) with the right trade for ${LABELS[docType] || docType}`,
        `No active contractor with the right trade for ${LABELS[docType] || docType} — the 20-day trigger will raise a "no contractor" alert instead of emailing`,
        'readiness-contractor')}
    </ul>
  );
}
